import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'
import FormLabel from '@mui/material/FormLabel'

type FormValues = Record<string, any>

type FormContextValue = {
  values: FormValues
  setFieldValue: (name: string, value: any) => void
  getFieldValue: (name: string) => any
  registerField: (name: string, initial?: any) => void
}

const FormContext = createContext<FormContextValue | null>(null)

function nameToKey(name?: string | number | (string | number)[]): string | null {
  if (name == null) return null
  if (Array.isArray(name)) return name.map(String).join('.')
  return String(name)
}

export interface FormProps {
  layout?: 'horizontal' | 'vertical' | 'inline'
  style?: React.CSSProperties
  className?: string
  children?: React.ReactNode
  onFinish?: (values: any) => void
  onFinishFailed?: (errorInfo: any) => void
  onFieldsChange?: (...args: any[]) => void
  initialValues?: any
  form?: any
  labelCol?: any
  wrapperCol?: any
  colon?: boolean
  name?: string
  [key: string]: any
}

const FormRoot: React.FC<FormProps> = ({
  layout = 'vertical',
  style,
  className,
  children,
  onFinish,
  onFinishFailed,
  onFieldsChange,
  initialValues,
  name: formName,
}) => {
  const [values, setValues] = useState<FormValues>(() => ({ ...(initialValues || {}) }))
  const registered = useRef<Set<string>>(new Set())

  const setFieldValue = useCallback(
    (fieldName: string, value: any) => {
      setValues((prev) => {
        const next = { ...prev, [fieldName]: value }
        onFieldsChange?.([{ name: [fieldName], value }], next)
        return next
      })
    },
    [onFieldsChange],
  )

  const getFieldValue = useCallback((fieldName: string) => values[fieldName], [values])

  const registerField = useCallback((fieldName: string, initial?: any) => {
    if (registered.current.has(fieldName)) return
    registered.current.add(fieldName)
    setValues((prev) => {
      if (Object.prototype.hasOwnProperty.call(prev, fieldName)) return prev
      const fromInitial = initialValues?.[fieldName]
      const seed = initial !== undefined ? initial : fromInitial !== undefined ? fromInitial : ''
      return { ...prev, [fieldName]: seed }
    })
  }, [initialValues])

  const ctx = useMemo(
    () => ({ values, setFieldValue, getFieldValue, registerField }),
    [values, setFieldValue, getFieldValue, registerField],
  )

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    try {
      onFinish?.({ ...values })
    } catch (err) {
      onFinishFailed?.(err)
    }
  }

  return (
    <FormContext.Provider value={ctx}>
      <Box
        component="form"
        name={formName}
        className={className}
        noValidate
        sx={{
          display: layout === 'inline' ? 'inline-flex' : 'block',
          flexWrap: layout === 'inline' ? 'wrap' : undefined,
          gap: layout === 'inline' ? 1 : undefined,
          ...style,
        }}
        onSubmit={handleSubmit}
      >
        {children}
      </Box>
    </FormContext.Provider>
  )
}

export interface FormItemProps {
  label?: React.ReactNode
  required?: boolean
  validateStatus?: 'success' | 'warning' | 'error' | 'validating' | ''
  help?: React.ReactNode
  style?: React.CSSProperties
  className?: string
  children?: React.ReactNode
  name?: string | number | (string | number)[]
  rules?: any
  initialValue?: any
  noStyle?: boolean
  extra?: React.ReactNode
  hidden?: boolean
  colon?: boolean
  labelCol?: any
  wrapperCol?: any
  valuePropName?: string
  trigger?: string
  getValueFromEvent?: (...args: any[]) => any
  [key: string]: any
}

const FormItem: React.FC<FormItemProps> = ({
  label,
  required,
  validateStatus,
  help,
  style,
  className,
  children,
  hidden,
  name,
  initialValue,
  noStyle,
  valuePropName = 'value',
  trigger = 'onChange',
  getValueFromEvent,
  ...rest
}) => {
  void rest
  const form = useContext(FormContext)
  const key = nameToKey(name)

  React.useEffect(() => {
    if (key && form) form.registerField(key, initialValue)
  }, [key, form, initialValue])

  if (hidden) return null

  const error = validateStatus === 'error'
  const childList = React.Children.toArray(children)
  const onlyChild = childList.length === 1 && React.isValidElement(childList[0]) ? childList[0] : null

  let mergedChild: React.ReactNode = children
  if (onlyChild) {
    const childProps: Record<string, any> = { ...(onlyChild.props || {}) }
    if (key && form) {
      const current = form.values[key]
      childProps[valuePropName] = current !== undefined ? current : onlyChild.props?.[valuePropName] ?? ''
      const originalTrigger = onlyChild.props?.[trigger]
      childProps[trigger] = (...args: any[]) => {
        let nextVal: any
        if (getValueFromEvent) {
          nextVal = getValueFromEvent(...args)
        } else if (valuePropName === 'checked') {
          const e = args[0]
          nextVal = e?.target ? e.target.checked : e
        } else {
          const e = args[0]
          if (e && e.target != null && typeof e.target.value !== 'undefined') {
            nextVal = e.target.value
          } else {
            nextVal = e
          }
        }
        form.setFieldValue(key, nextVal)
        if (typeof originalTrigger === 'function') originalTrigger(...args)
      }
    }
    mergedChild = React.cloneElement(onlyChild, childProps)
  } else if (childList.length === 0) {
    mergedChild = null
  } else {
    // Nhiều child / text — render nguyên, không clone (tránh Children.only crash)
    mergedChild = <>{childList}</>
  }

  if (noStyle) return <>{mergedChild}</>

  return (
    <FormControl
      fullWidth
      error={error}
      required={required}
      className={['ant-form-item', className].filter(Boolean).join(' ')}
      sx={{
        mb: 0,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        ...style,
      }}
    >
      {label != null && label !== '' && (
        <FormLabel
          required={required}
          error={error}
          sx={{
            position: 'static',
            transform: 'none',
            mb: '4px',
            fontSize: 13,
            fontWeight: 500,
            lineHeight: 1.2,
            color: 'text.primary',
            whiteSpace: 'normal',
          }}
        >
          {label}
        </FormLabel>
      )}
      {mergedChild}
      {help != null && help !== '' && (
        <FormHelperText className={error ? 'ant-form-item-explain-error' : undefined}>{help}</FormHelperText>
      )}
    </FormControl>
  )
}

export type FormComponent = React.FC<FormProps> & { Item: typeof FormItem }

export const Form = FormRoot as FormComponent
Form.Item = FormItem

export default Form
